import { Sprout, CircleCheck, Award, Shield, Leaf, ClipboardCheck, Users, TrendingUp } from "lucide-react";

const CERT_IMG = "https://customer-assets.emergentagent.com/job_meal-manager-37/artifacts/ls65y5w5_image.png";

const t = {
  fr: {
    connectTitle: "EcoLunch Connect",
    connectSubtitle: "Service de Lancement Clé en Main",
    connectBadge: "De Zéro au Lancement — Entièrement Géré par EcoLunch",
    connectDesc: "Un service clé en main entièrement géré qui met en lien les écoles sans service de repas avec des traiteurs certifiés EcoLunch. EcoLunch pilote tout le processus — de la certification des traiteurs et du jumelage, à l'implantation complète, la formation et la supervision du lancement — pendant que l'école choisit simplement parmi les fournisseurs pré-qualifiés. Tout est piloté, implanté et supervisé par EcoLunch jusqu'à la mise en fonction complète.",
    connectSteps: [
      { icon: CircleCheck, label: "Certification" },
      { icon: Users, label: "Jumelage" },
      { icon: Sprout, label: "Formation" },
      { icon: Shield, label: "Lancement" },
    ],
    connectAdvSchools: "Avantages pour les Écoles :",
    connectAdvSchoolsList: ["Aucune connaissance technique requise", "Traiteurs pré-qualifiés seulement", "Support complet EcoLunch", "Déploiement rapide"],
    connectAdvCaterers: "Avantages pour les Traiteurs :",
    connectAdvCaterersList: ["Crédibilité instantanée", "Nouveaux contrats écoles", "Marketing EcoLunch", "Opportunités de croissance"],
    certTitle: "Certification FutÉcoLuncH",
    certSubtitle: "Normes de Qualité et Confiance",
    certDesc: "La Certification EcoLunch garantit que seuls des traiteurs rigoureusement évalués — opérant selon un processus rigoureusement structuré et conforme aux standards les plus élevés du secteur. Grâce à cette certification, EcoLunch établit une norme de qualité transparente, mesurable et reconnue — afin que chaque partenaire contribue à un environnement alimentaire sain, responsable et cohérent.",
    certCriteria: "Critères de Certification",
    certCards: [
      { icon: Shield, title: "Licence et Conformité", desc: "Licences alimentaires valides et conformité réglementaire" },
      { icon: Leaf, title: "Pratiques Durables", desc: "Engagement envers des opérations écoresponsables et durables" },
      { icon: CircleCheck, title: "Normes de Sécurité Alimentaire", desc: "Conformité aux normes de sécurité alimentaire applicables, incluant les exigences du MAPAQ et de l'ACIA" },
      { icon: ClipboardCheck, title: "Expérience Restauration Scolaire", desc: "Expérience prouvée en programmes de repas d'écoles primaires" },
      { icon: Users, title: "Excellence de Service", desc: "Engagement service client et garantie de qualité" },
      { icon: TrendingUp, title: "Capacité Opérationnelle", desc: "Capacité de production et livraison vérifiée" },
    ],
  },
  en: {
    connectTitle: "EcoLunch Connect",
    connectSubtitle: "Turnkey Launch Service",
    connectBadge: "From Zero to Launch — Fully Managed by EcoLunch",
    connectDesc: "A fully managed turnkey service that connects schools without meal service to EcoLunch-certified caterers. EcoLunch drives the entire process — from caterer certification and matching, to full implementation, training and launch supervision — while the school simply chooses from pre-qualified providers.",
    connectSteps: [
      { icon: CircleCheck, label: "Certification" },
      { icon: Users, label: "Matching" },
      { icon: Sprout, label: "Training" },
      { icon: Shield, label: "Launch" },
    ],
    connectAdvSchools: "Benefits for Schools:",
    connectAdvSchoolsList: ["No technical knowledge required", "Pre-qualified caterers only", "Full EcoLunch support", "Fast deployment"],
    connectAdvCaterers: "Benefits for Caterers:",
    connectAdvCaterersList: ["Instant credibility", "New school contracts", "EcoLunch marketing", "Growth opportunities"],
    certTitle: "FutEcoLuncH Certification",
    certSubtitle: "Quality Standards and Trust",
    certDesc: "The EcoLunch Certification ensures only rigorously evaluated caterers — operating according to the highest industry standards. Through this certification, EcoLunch establishes a transparent, measurable and recognized quality standard.",
    certCriteria: "Certification Criteria",
    certCards: [
      { icon: Shield, title: "License & Compliance", desc: "Valid food licenses and regulatory compliance" },
      { icon: Leaf, title: "Sustainable Practices", desc: "Commitment to eco-responsible operations" },
      { icon: CircleCheck, title: "Food Safety Standards", desc: "Compliance with applicable food safety standards, including the requirements of MAPAQ and the CFIA" },
      { icon: ClipboardCheck, title: "School Catering Experience", desc: "Proven experience in school meal programs" },
      { icon: Users, title: "Service Excellence", desc: "Customer service commitment and quality guarantee" },
      { icon: TrendingUp, title: "Operational Capacity", desc: "Verified production and delivery capacity" },
    ],
  },
};

export default function ConnectCertSection({ lang }) {
  const c = t[lang];
  return (
    <>
      <section id="connect" data-testid="connect-section" className="py-16 sm:py-24 bg-darker">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 sm:mb-16">
            <div className="inline-flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-primary/20 mb-6 glow-primary">
              <Sprout className="h-8 w-8 sm:h-10 sm:w-10 text-primary" />
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">{c.connectTitle}</h2>
            <p className="text-lg sm:text-xl text-gradient-lime font-bold">{c.connectSubtitle}</p>
          </div>
          <div className="text-center mb-8">
            <div className="inline-flex items-center rounded-md border bg-primary/20 text-primary border-primary/40 font-bold text-sm sm:text-base px-4 sm:px-6 py-2 sm:py-3">{c.connectBadge}</div>
          </div>
          <div className="max-w-4xl mx-auto mb-12">
            <div className="rounded-xl border text-card-foreground shadow bg-dark border-primary/30">
              <div className="p-6 py-6 sm:py-8">
                <p className="text-base sm:text-lg text-white/80 leading-relaxed px-4">{c.connectDesc}</p>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6 max-w-4xl mx-auto mb-12">
            {c.connectSteps.map((step) => {
              const Icon = step.icon;
              return (
                <div key={step.label} className="rounded-xl border text-card-foreground shadow bg-dark border-primary/30 hover:border-primary transition-all duration-300 hover-lift">
                  <div className="p-4 sm:p-6 text-center">
                    <div className="inline-flex items-center justify-center w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-primary/20 mb-3 glow-primary">
                      <Icon className="h-5 w-5 sm:h-6 sm:w-6 text-primary" />
                    </div>
                    <p className="text-sm sm:text-base font-bold text-white">{step.label}</p>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8 max-w-4xl mx-auto">
            <div className="rounded-xl border text-card-foreground shadow bg-dark border-primary/20">
              <div className="p-6">
                <h4 className="text-lg font-bold text-gradient-lime mb-4">{c.connectAdvSchools}</h4>
                <ul className="space-y-2">
                  {c.connectAdvSchoolsList.map((item) => (
                    <li key={item} className="flex items-start gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-primary flex-shrink-0 mt-2 glow-primary" />
                      <span className="text-sm text-white/70">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            <div className="rounded-xl border text-card-foreground shadow bg-dark border-primary/20">
              <div className="p-6">
                <h4 className="text-lg font-bold text-gradient-lime mb-4">{c.connectAdvCaterers}</h4>
                <ul className="space-y-2">
                  {c.connectAdvCaterersList.map((item) => (
                    <li key={item} className="flex items-start gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-primary flex-shrink-0 mt-2 glow-primary" />
                      <span className="text-sm text-white/70">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>
      <section id="certification" data-testid="certification-section" className="py-16 sm:py-24 bg-soft">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 sm:mb-16">
            <div className="inline-flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-primary/20 mb-6 glow-strong">
              <Award className="h-8 w-8 sm:h-10 sm:w-10 text-primary" />
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">{c.certTitle}</h2>
            <p className="text-lg sm:text-xl text-gradient-lime font-bold">{c.certSubtitle}</p>
          </div>
          <div className="max-w-4xl mx-auto mb-12">
            <div className="rounded-xl border text-card-foreground shadow bg-dark border-primary/30">
              <div className="p-6 py-6 sm:py-8 flex flex-col sm:flex-row items-center gap-6">
                <div className="flex-shrink-0 bg-primary/10 rounded-xl p-4">
                  <img src={CERT_IMG} alt="Certification" className="h-16 sm:h-20 w-auto" />
                  <p className="text-xs text-center font-bold text-primary mt-2">CERTIFICATION</p>
                  <p className="text-xs text-center text-white/60">FutÉcoLuncH</p>
                </div>
                <p className="text-base sm:text-lg text-white/80 leading-relaxed">{c.certDesc}</p>
              </div>
            </div>
          </div>
          <h3 className="text-2xl sm:text-3xl font-bold text-white text-center mb-8">{c.certCriteria}</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 max-w-5xl mx-auto">
            {c.certCards.map((card) => {
              const Icon = card.icon;
              return (
                <div key={card.title} className="rounded-xl border text-card-foreground shadow hover-lift bg-dark border-primary/30 hover:border-primary transition-all duration-300">
                  <div className="p-6">
                    <div className="inline-flex items-center justify-center w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-primary/20 mb-4 glow-primary">
                      <Icon className="h-5 w-5 sm:h-6 sm:w-6 text-primary" />
                    </div>
                    <h4 className="text-lg font-bold text-white mb-2">{card.title}</h4>
                    <p className="text-sm text-white/70">{card.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>
    </>
  );
}
